import { Injectable, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AlertController, LoadingController, NavController } from '@ionic/angular';
import { HelperService } from './helper.service';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Group, Device, Sms, UserInfo, User, UserInvited, DeviceDB, toGroupDB, RoleType, GroupDB, UserNameRole, EMPTY_GROUP, STATUS_ACTIVE, CompactUser } from './models';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom, of } from 'rxjs';
import { Globals } from '../services/app-globals';
import { ADMIN1, ADMIN2 } from "./models";
import { catchError, defaultIfEmpty, map, switchMap } from 'rxjs/operators';
import { collectionGroup, query, where, getDocs, onSnapshot, Timestamp } from 'firebase/firestore';
import firebase from 'firebase/compat/app';
import { v4 as uuidv4 } from 'uuid';
import * as _ from 'lodash';
import { ValidationErrors } from '@angular/forms';
import { environment } from 'src/environments/environment';



@Injectable({
  providedIn: 'root'
})
export class FireService implements OnDestroy {

  private userInfo: UserInfo = {
    claims: null, email: "", name: "", doc: null, uid: "", admin: false
  };

  private userInfoSubject = new BehaviorSubject<UserInfo>(this.userInfo);
  // private userInfoSubject = new ReplaySubject<UserInfo>(1); // No initial value is emitted
  userInfo$ = this.userInfoSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  public vm$: Observable<{ loading: boolean; userInfo: UserInfo }>;

  private userDocUnsubscribe: (() => void) | null = null;

  constructor(
    private firestore: AngularFirestore,
    private angularFireFunctions: AngularFireFunctions,
    public helper: HelperService,
    public alertController: AlertController,
    public navCtrl: NavController,
    private fireAuth: AngularFireAuth,
    private loadingCtrl: LoadingController,
  ) {
    this.vm$ = combineLatest([this.loading$, this.userInfo$]).pipe(
      map(([loading, userInfo]) => ({ loading, userInfo }))
    );

    this.loadingSubject.next(true);

    this.fireAuth.authState.subscribe(async (authState) => {
      if (authState) {
        console.log("email at boot: ", authState.email);
        try {
          await this.initializeUserInfo(authState);
        } catch (error) {
          this.resetStateAndSignOut();
        } finally {
          this.loadingSubject.next(false);
        }
      }
    });
  }

  adminUser$(): Observable<boolean> {
    return this.userInfo$.pipe(
      map((userInfo) => {
        if (!userInfo || !userInfo.claims) {
          return false; // User or claims are undefined
        }

        const isAdmin = userInfo.claims.al === ADMIN1 || userInfo.claims.al === ADMIN2;

        // console.log(`User is admin: ${isAdmin}`);
        return isAdmin;
      }),
      catchError((error) => {
        // console.error('Error in adminUser$:', error);
        return of(false); // In case of error, assume the user is not an admin
      }),
      defaultIfEmpty(false)
    );
  }


  adminUser(claim: string): boolean {
    if ((claim === ADMIN1) || (claim === ADMIN2)) {
      console.log("user is admin");
    }
    return (claim === ADMIN1) || (claim === ADMIN2);
  }


  private async initializeUserInfo(authState: firebase.User) {
    this.userInfo.email = authState.email ?? "";
    this.userInfo.uid = authState.uid;
    const token = await authState.getIdTokenResult();
    this.userInfo.claims = token.claims;
    // console.log("claims", this.userInfo.claims);

    try {

      // console.log('setting claims on user: ', authState.email);
      // await this.setClaimsOnUser(user.email, ADMIN1);
      // await this.saveClaimsOnUser(authState.email, ADMIN1);

      if (this.userInfo.claims.al && typeof this.userInfo.claims.al === "string") {
        this.userInfo.admin = this.adminUser(this.userInfo.claims.al);
      } else {
        this.userInfo.admin = false; // Default to non-admin if claims are missing or invalid
      }
    } catch (error) {
      console.error("Error checking admin status:", error);
      this.userInfo.admin = false;
    }

    const userDocFull = await this.getDocByFieldWithRetry(Globals.DB.USERS, 'uid', this.userInfo.uid);
    if (!userDocFull) {
      throw new Error("Error loading user profile");
    }

    this.userDocUnsubscribe = onSnapshot(userDocFull.ref, (doc: any) => {
      this.userInfo.doc = doc.data();
      this.userInfo.name = this.userInfo.doc?.name ?? "";
      // console.log("userInfo", this.userInfo);
      this.userInfoSubject.next(this.userInfo);
    });
  }

  private resetStateAndSignOut() {
    this.userInfoSubject.next({ claims: null, email: "", name: "", doc: null, uid: "", admin: false });
    this.fireAuth.signOut().then(() => {
      this.helper.toast("Error loading user profile. Please sign in again.");
      this.ngOnDestroy();
      this.navCtrl.navigateRoot('/');
    });
  }

  unsubscribeUserDoc() {
    if (this.userDocUnsubscribe) {
      this.userDocUnsubscribe();
    }
  }

  ngOnDestroy() {
    console.log('FireService is being destroyed');
    this.unsubscribeUserDoc();
    this.resetState(); // Reset internal state when destroyed
  }

  private resetState() {
    this.userInfo = { claims: null, email: "", name: "", doc: null, uid: "", admin: false };
    this.userInfoSubject.next(this.userInfo);
    this.loadingSubject.next(false);
  }



  /****************************************************************************************************
  
   ######   ######## ######## 
  ##    ##  ##          ##    
  ##        ##          ##    
  ##   #### ######      ##    
  ##    ##  ##          ##    
  ##    ##  ##          ##    
   ######   ########    ##    
  
  ****************************************************************************************************/

  async getDocByFieldWithRetry(
    collection: string,
    field: string,
    value: string,
    maxRetries: number = 40,
    delayMs: number = 500
  ): Promise<{ data: any; ref: any } | null> {
    let retries = 0;

    while (retries < maxRetries) {
      try {
        // Perform the query to check if the document exists
        const querySnapshot = await this.firestore
          .collection(collection, ref => ref.where(field, '==', value))
          .get()
          .toPromise();

        // If document exists, return it
        if (querySnapshot && !querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          return { data: doc.data(), ref: doc.ref };
        }

        // If no document is found, increment retry count and wait
        retries++;
        console.log(`No document found. Retrying... (${retries}/${maxRetries})`);
        await this.delay(delayMs);
      } catch (error) {
        console.error(`Error retrieving document from collection: ${collection}`, error);
        return null;
      }
    }

    // Return null if max retries are exceeded
    console.log(`Document not found after ${maxRetries} retries.`);
    return null;
  }

  // Utility function to add a delay
  delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }


  async getDocByField(collection: string, field: string, value: string): Promise<{ data: any; ref: any } | null> {
    try {
      // Step 1: Query the Firestore collection where the 'uid' field matches the given uid
      const querySnapshot = await this.firestore
        .collection(collection, ref => ref.where(field, '==', value))
        .get()
        .toPromise();

      // Step 2: Handle cases where no document or multiple documents are found
      if (!querySnapshot || querySnapshot.empty) {
        console.log(`No document found`);
        return null;
      }

      if (querySnapshot.size > 1) {
        throw new Error(`Multiple documents found`);
      }

      // Step 3: Get the first matching document
      const doc = querySnapshot.docs[0];

      // Step 4: Return the document data and reference
      return { data: doc.data(), ref: doc.ref };

    } catch (error) {
      // Handle errors
      console.error(`Error getting document`, error);
      return null;
    }
  }




  async getDoc(collection: string, id: string): Promise<{ data: any; ref: any } | null> {

    // Reference the user's document directly
    const userDocRef = this.firestore.collection(collection).doc(id);

    try {
      // Get the document snapshot using firstValueFrom
      const docSnapshot = await firstValueFrom(userDocRef.get());

      // Check if the document exists
      if (docSnapshot.exists) {
        return { data: docSnapshot.data(), ref: userDocRef.ref };
      } else {
        console.log(`Document does not exist: ${collection}/${id}`);
        return null;
      }
    } catch (error) {
      console.error(`Error getting document: ${collection}/${id}`, error);
      return null;
    }
  }


  // this function returns the whole doc including ref
  // so the caller can extract the path of the document
  async getDeviceDoc(deviceId: string): Promise<any> {
    const q = query(collectionGroup(this.firestore.firestore, Globals.DB.FLEET), where('deviceId', '==', deviceId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log("no device with this id");
      return null;
    }

    // if snapshot has more than one doc, something is wrong
    if (querySnapshot.size > 1) {
      console.log("Something is wrong, there is more than one device with the same id");
      return null;
    }

    return querySnapshot.docs[0];
  }

  fetchDeviceById(deviceId: string): Observable<Device> {
    return this.firestore.collection<Device>(Globals.DB.FLEET, ref =>
      ref.where('id', '==', deviceId)
    ).valueChanges({ idField: 'firestoreDocId' })
      .pipe(
        map(devices => {
          // Ensure there is at least one device in the array
          if (devices.length > 0) {
            return devices[0];  // Return the first device (expected to be unique)
          } else {
            throw new Error(`Device with id ${deviceId} not found.`);
          }
        })
      );
  }


  getDataDeviceTidal(device: Device, startDate: Date, endDate: Date): Observable<any[]> {

    return this.userInfo$.pipe(
      switchMap((userInfo) => {
        if (!userInfo) {
          return of([]);
        }

        this.loadingSubject.next(true);

        const tStart = firebase.firestore.Timestamp.fromDate(startDate);
        const tEnd = firebase.firestore.Timestamp.fromDate(endDate);

        try {

          // Query the devices collection with the specified constraints
          return this.firestore.collection(`/${Globals.DB.FLEET}/${device.firestoreDocId}/logs`, (ref) =>
            ref
              .where('t', '>=', tStart)
              .where('t', '<=', tEnd)
              .orderBy('t')
          ).get().pipe(
            map((snapshot) => {
              if (snapshot.empty) {
                this.loadingSubject.next(false);
                console.log(`No data found for device ${device.id}`);
                return [];
              }

              this.loadingSubject.next(false);

              // Extract data from Firestore documents
              return snapshot.docs.map((doc) => doc.data());
            }),
            catchError((error) => {
              console.error(`Error querying device data:`, error);
              this.loadingSubject.next(false);
              return of([]);
            }),
          );

        } catch (error) {
          this.loadingSubject.next(false);
          return of([]);
        }

      })
    );
  }



  getDataDeviceAti(device: Device, startDate: Date, endDate: Date): Observable<any[]> {

    return this.userInfo$.pipe(
      switchMap((userInfo) => {
        if (!userInfo) {
          return of([]);
        }

        this.loadingSubject.next(true);

        const tStart = firebase.firestore.Timestamp.fromDate(startDate);
        const tEnd = firebase.firestore.Timestamp.fromDate(endDate);

        try {

          // Query the devices collection with the specified constraints
          return this.firestore.collection(`/${Globals.DB.FLEET}/${device.firestoreDocId}/logs`, (ref) =>
            ref
              .where('t', '>=', tStart)
              .where('t', '<=', tEnd)
              .where('e', 'in', ['s', 'l']) // Using `in` operator for logical OR
              .orderBy('t')
          ).get().pipe(
            map((snapshot) => {
              if (snapshot.empty) {
                this.loadingSubject.next(false);
                console.log(`No data found for device ${device.id}`);
                return [];
              }

              this.loadingSubject.next(false);

              // Extract data from Firestore documents
              return snapshot.docs.map((doc) => doc.data());
            }),
            catchError((error) => {
              console.error(`Error querying device data:`, error);
              this.loadingSubject.next(false);
              return of([]);
            }),
          );

        } catch (error) {
          this.loadingSubject.next(false);
          return of([]);
        }

      })
    );
  }


  downloadDevices(): Observable<Device[]> {
    return this.userInfo$.pipe(
      switchMap(userInfo => {

        if (!userInfo) {
          return of([]) as Observable<Device[]>;
        }

        // Admins can fetch all devices
        if (this.userInfo.admin) {
          return this.firestore
            .collection<Device>(Globals.DB.FLEET)
            .valueChanges({ idField: 'firestoreDocId' }) as Observable<Device[]>;
        }


        // Non-admins can only fetch devices they are associated with
        const currentUserUid = userInfo.uid;

        return this.firestore
          .collection<Device>(Globals.DB.FLEET, (ref) =>
            ref.where('users', 'array-contains', currentUserUid)
          )
          .valueChanges({ idField: 'firestoreDocId' }) as Observable<Device[]>;
      }),
      catchError((error) => {
        console.error('Error downloading devices:', error);
        // Return an empty array in case of an error
        return of([]) as Observable<Device[]>;
      })
    );
  }



  downloadGroups(): Observable<Group[]> {
    return this.userInfo$.pipe(
      switchMap(userInfo => {

        if (!userInfo) {
          return of([]) as Observable<Group[]>;
        }

        // Admins can fetch all organizations
        if (userInfo.admin) {
          return this.firestore
            .collection<Group>(Globals.DB.GROUPS)
            .valueChanges({ idField: 'firestoreDocId' }) as Observable<Group[]>;
        }

        // Non-admins can only fetch organizations they are associated with
        const currentUserUid = userInfo.uid;

        return this.firestore.collection<Group>(Globals.DB.GROUPS, ref =>
          ref.where('users', 'array-contains', currentUserUid)
        ).valueChanges({ idField: 'firestoreDocId' }) as Observable<Group[]>;
      }),
      catchError((error) => {
        console.error('Error downloading organizations:', error);
        // Return an empty array in case of an error
        return of([]) as Observable<Group[]>;
      })
    );
  }


  downloadUsers(): Observable<User[]> {
    return this.userInfo$.pipe(
      switchMap(userInfo => {

        if (!userInfo) {
          return of([]) as Observable<User[]>;
        }

        // Admins can fetch all users
        if (this.userInfo.admin) {
          return this.firestore
            .collection<User>(Globals.DB.USERS)
            .valueChanges({ idField: 'firestoreDocId' }) as Observable<User[]>;
        }
        else {
          // Non-admins have nothing to do here since they cannot call this function
          return of([]) as Observable<User[]>;
        }

      }),
      catchError((error) => {
        console.error('Error downloading users:', error);
        // Return an empty array in case of an error
        return of([]) as Observable<User[]>;
      })
    );
  }

  /****************************************************************************************************
  
  ######     ###    ##     ## ######## 
  ##    ##   ## ##   ##     ## ##       
  ##        ##   ##  ##     ## ##       
   ######  ##     ## ##     ## ######   
        ## #########  ##   ##  ##       
  ##    ## ##     ##   ## ##   ##       
   ######  ##     ##    ###    ######## 
   
  ****************************************************************************************************/

  public async inviteUser(userInvited: UserInvited): Promise<boolean> {

    if (userInvited.email === "") {
      throw new Error("Missing parameters");
    }

    const spinner = await this.loadingCtrl.create({});
    spinner.present();

    // invite the user
    try {
      const result = await this.inviteUserCloudFx(userInvited).toPromise();
      await this.helper.toast("User invited.");
      return true;
    } catch (error) {
      console.error('Error inviting user:', error);
      await this.helper.toast("Error inviting user.");
      return false;
    }
    finally {
      spinner.dismiss();
    }

  }

  // claim can be ADMIN1 or ADMIN2 (or '' to remove the claim)
  public async setClaimsOnUser(email: string, claim: string) {

    if (email === "") {
      throw new Error("Missing parameters");
    }

    this.loadingSubject.next(true);

    try {
      const result = await this.setClaimsOnUserCloudFx(email, claim).toPromise();
      if (claim === "") {
        await this.helper.toastFull("Privilege removed. Next time the user signs in the privilege will be removed.", "", 5000);
      }
      else {
        await this.helper.toastFull("Privilege set. Next time the user signs in the privilege will be set.", "", 5000);
      }
    } catch (error) {
      if (claim === "") {
        await this.helper.toast("Error removing privilege");
      } else {
        await this.helper.toast("Error setting privilege");
      }

      console.error('Error setting claims:', error);
      throw new Error("Error setting claims: " + error);
    }
    finally {
      this.loadingSubject.next(false);
    }

  }


  public async addUserToGroup(userInvited: UserInvited): Promise<boolean> {

    if (userInvited.email === "") {
      throw new Error("Missing parameters");
    }

    const spinner = await this.loadingCtrl.create({});
    spinner.present();


    try {
      const result = await this.addUserToGroupCloudFx(userInvited).toPromise();
      await this.helper.toast("User added to group.");
      return true;
    } catch (error) {
      console.error('Error adding user to group:', error);
      await this.helper.toast("Error adding user to group.");
      return false;
    }
    finally {
      spinner.dismiss();
    }

  }

  // removes a user from the organization and all its devices
  public async removeUserFromGroupAndDevices(userUid: string, orgId: string) {

    if ((userUid === "") || (orgId === "")) {
      throw new Error("Missing parameters");
    }

    const spinner = await this.loadingCtrl.create({});
    spinner.present();

    // remove the user
    try {
      const result = await this.removeUserFromGroupAndDevicesCloudFx(userUid, orgId).toPromise();
      await this.helper.toast("User removed.");
    } catch (error: any) {
      if (error.code === "not-found") {
        console.error("Error:", error.message);
        await this.helper.toast("Error removing user.");
        throw new Error("Error removing user: " + error);
      } else {
        console.error('Error removing user:', error);
        await this.helper.toast("Error removing user.");
        throw new Error("Error removing user: " + error);
      }
    }
    finally {
      spinner.dismiss();
    }

  }



  // deletes a user from the system
  public async deleteUser(email: string) {

    if (email === "") {
      this.helper.toast(`Please provide an email`);
      throw new Error("Email cannot be empty");
    }

    const spinner = await this.loadingCtrl.create({});
    spinner.present();

    try {
      const result = await this.deleteUserCloudFx(email).toPromise();
      await this.helper.toast("User deleted.");
    } catch (error: any) {
      console.error('Error removing user:', error);
      await this.helper.toast("Error removing user.");
      throw new Error("Error removing user: " + error);
    }
    finally {
      spinner.dismiss();
    }

  }


  // deletes a group and all its devices
  public async deleteGroup(groupId: string) {

    if (groupId === "") {
      this.helper.toast(`Please provide a group`);
      throw new Error("Group id cannot be empty");
    }

    const spinner = await this.loadingCtrl.create({});
    spinner.present();

    // remove the group
    try {
      const result = await this.deleteGroupCloudFx(groupId).toPromise();
      await this.helper.toast("Group deleted.");
    } catch (error: any) {
      if (error.code === "not-found") {
        console.error("Error:", error.message);
        throw new Error("Error deleting group: " + error);
      } else {
        console.error('Error deleting group:', error);
        await this.helper.toast("Error deleting group.");
        throw new Error("Error deleting group: " + error);
      }
    }
    finally {
      spinner.dismiss();
    }
  }


  // Query Firestore to check:
  // if the serial number exists
  // if the device is not already claimed (claimedByUid is null)
  checkDeviceCanBeClaimed(serialNumber: string): Observable<ValidationErrors | null> {
    return this.firestore
      .collection(Globals.DB.FLEET, (ref) =>
        ref.where('serialNumber', '==', serialNumber)
          .limit(2))
      .get()
      .pipe(
        map((snapshot) => {

          // No matching serial number found
          // More than one matching serial number found (duplicates)
          if (snapshot.size !== 1) {
            return { invalidSerialNumber: true };
          }

          const deviceData = snapshot.docs[0].data() as DeviceDB;

          // Check if the device is not already claimed
          if (deviceData.claimedByUid) {
            console.log(`This device has already been claimed.`);
            return { invalidSerialNumber: true };
          }

          // Exactly one matching serial number found
          return null;
        }),
        catchError(() => of({ invalidSerialNumber: true })) // Handle errors gracefully
      );
  }


  // Query Firestore to check:
  // if the group name exists for a user
  checkGroupDoesNotExist(groupName: string, userUid: string): Observable<ValidationErrors | null> {

    console.log("checkGroupDoesNotExist");

    return this.firestore
      .collection(Globals.DB.GROUPS, (ref) =>
        ref
          .where('users', 'array-contains', userUid)
          .where('name', '==', groupName)
          .limit(1))
      .get()
      .pipe(
        map((snapshot) => {
          if (snapshot.size === 0) {
            return null; // No error, group name is available
          }
          return { invalidGroupName: true };
        }),
        catchError(() => of({ invalidGroupName: true })) // Handle errors gracefully
      );
  }


  checkEmailDoesNotExist(email: string): Observable<ValidationErrors | null> {
    return this.firestore
      .collection(Globals.DB.USERS, ref => ref.where('email', '==', email).limit(1))
      .get()
      .pipe(
        map(snapshot => (snapshot.size === 0 ? null : { emailAlreadyThere: true })), // Return error only if email exists
        catchError(() => of(null)) // Prevent breaking due to Firestore errors
      );
  }


  saveNote(device: Device, rawT: Timestamp, note: string) {
    this.loadingSubject.next(true); // Indicate the loading state starts

    console.log(`Updating note for device ${device.id}, docId ${device.firestoreDocId}`);

    this.firestore
      .collection(`/${Globals.DB.FLEET}/${device.firestoreDocId}/logs`, (ref) =>
        ref.where('t', '>=', rawT).limit(1) // Use limit(1) since you expect only one document
      )
      .get()
      .subscribe(
        (querySnapshot) => {
          if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0]; // Get the first (and expected only) document
            doc.ref
              .update({ note: note })
              .then(() => {
                console.log(`Note updated for document ${doc.id}`);
                this.helper.toast("Note successfully updated."); // Success feedback
              })
              .catch((error) => {
                console.error(`Failed to update note for document ${doc.id}:`, error);
                this.helper.toast("Failed to update the note. Please try again."); // Error feedback
              })
              .finally(() => {
                this.loadingSubject.next(false); // Indicate the loading state ends
              });
          } else {
            console.warn("No matching document found to update the note.");
            this.helper.toast("Failed to update the note. Please try again."); // Warning feedback
            this.loadingSubject.next(false); // Indicate the loading state ends
          }
        },
        (error) => {
          console.error("Failed to retrieve logs:", error);
          this.helper.toast("Failed to retrieve the logs. Please try again."); // Error feedback
          this.loadingSubject.next(false); // Indicate the loading state ends
        }
      );
  }



  // this will claim a device to a customer account
  // the device will be already created/onboarded by an admin
  // so the device document will be created already
  // when a customer claims a device, we will add the customer's uid to the device document
  public async claimDevice(serialNumber: string, name: string, userUid: string): Promise<boolean> {

    if (name === "") {
      this.helper.toast(`Please provide a name`);
      throw new Error("Device name cannot be empty");
    }

    if (serialNumber === "") {
      this.helper.toast(`Please provide a serial number`);
      throw new Error("Serial number cannot be empty");
    }

    if (userUid === "") {
      this.helper.toast(`Please provide a user`);
      throw new Error("User uid cannot be empty");
    }

    this.loadingSubject.next(true);

    try {
      // Step 1: Search for the device by serial number
      const deviceSnapshot = await this.firestore.firestore
        .collection(Globals.DB.FLEET)
        .where('serialNumber', '==', serialNumber)
        .limit(2)
        .get();

      if (deviceSnapshot.empty) {
        this.helper.toast(`Device with serial number ${serialNumber} was not found.`);
        throw new Error(`Device with serial number ${serialNumber} does not exist.`);
      }

      if (deviceSnapshot.size > 1) {
        this.helper.toast(`Multiple devices found with serial number ${serialNumber}. Contact support.`);
        throw new Error(`Multiple devices found with serial number ${serialNumber}.`);
      }

      const deviceDoc = deviceSnapshot.docs[0];
      const deviceData = deviceDoc.data() as DeviceDB;

      // Step 2: Check if the device is already claimed
      if (deviceData.claimedByUid) {
        this.helper.toast(`This device has already been claimed.`);
        throw new Error(`Device ${serialNumber} has already been claimed.`);
      }

      // Step 3: Update the device with customer information
      await deviceDoc.ref.update({
        name: name,
        claimedByUid: userUid,
        claimedAt: firebase.firestore.FieldValue.serverTimestamp(),
        users: firebase.firestore.FieldValue.arrayUnion(userUid),
      });

      this.helper.toast(`${name} has been successfully claimed.`);
      return true;

    } catch (error) {
      console.log("Error adding device:", error);
      this.helper.toast(
        `We couldn't claim ${name}. Please try again or contact support if the issue persists.`
      );
      return false;

    } finally {
      this.loadingSubject.next(false);
    }
  }



  public async shareDeviceWithGroup(device: Device, groupId: string): Promise<boolean> {
    console.log(`Sharing device ${device.id} with group ${groupId}`);
    this.loadingSubject.next(true);

    const batch = this.firestore.firestore.batch(); // Start a Firestore batch

    try {
      // Step 1: Query Firestore to find the organization document by the field "id" (not the Firestore document ID)
      const groupSnapshot = await this.firestore.firestore.collection(Globals.DB.GROUPS)
        .where('id', '==', groupId)
        .limit(2) // We expect only one result, but we want to fail if there are multiple
        .get();

      if (groupSnapshot.size > 1) {
        throw new Error(`Multiple groups found with id ${groupId}. Operation aborted.`);
      }

      if (groupSnapshot.empty) {
        throw new Error(`group with id ${groupId} not found`);
      }

      // Step 2: Get the actual Firestore document reference for the group
      const groupDocRef = groupSnapshot.docs[0].ref;
      const groupData = groupSnapshot.docs[0].data();

      // Step 3: Retrieve the users array from the group (ensure it's an array)
      const groupUsers: string[] = groupData['users'] || [];

      // Step 4: Add the deviceId to the devices array of the group using arrayUnion (handled in batch)
      batch.update(groupDocRef, {
        devices: firebase.firestore.FieldValue.arrayUnion(device.id)
      });

      // Step 5: Get the device document reference
      const deviceSnapshot = await this.firestore.firestore.collection(Globals.DB.FLEET)
        .where('id', '==', device.id)
        .limit(2) // We expect only one result, but we want to fail if there are multiple
        .get();

      if (deviceSnapshot.size > 1) {
        throw new Error(`Multiple devices found with id ${device.id}. Operation aborted.`);
      }

      if (deviceSnapshot.empty) {
        throw new Error(`Device with id ${device.id} not found`);
      }

      const deviceDocRef = deviceSnapshot.docs[0].ref;
      const deviceData = deviceSnapshot.docs[0].data();
      const deviceUsersOrg: Array<{ user: string; org: string }> = deviceData['usersOrg'] || [];

      // Step 6: add the org users to the device->usersOrg array
      groupUsers.forEach((user) => deviceUsersOrg.push({ user, org: groupId }));
      const updatedUsersOrg = [...deviceUsersOrg];

      // Step 7: recreate the users array for the device
      const updatedUsers = this.recreateDeviceUsers({ claimedByUid: device.claimedByUid, usersOrg: updatedUsersOrg });

      // Step 8: Add the updates to the batch for the device document
      batch.update(deviceDocRef, {
        users: updatedUsers,
        usersOrg: updatedUsersOrg
      });

      // Step 9: Commit the batch
      await batch.commit();

      console.log(`Device ${device.id} successfully shared with group ${groupId}`);
      this.helper.toast(`Device ${device.name} successfully shared.`);
      return true;

    } catch (error) {
      this.helper.toast(`Failed to share device.`);
      console.error(`Error sharing device with group ${groupId}:`, error);
      throw new Error(`Failed to share device with group: ${error}`);
      return false;

    } finally {
      this.loadingSubject.next(false);
    }
  }

  // this function also exists in users.ts (cloud functions)
  // the normal way for maintaining the users arrays in devices is:
  // - add the claimedByUid user (the owner) to the users array
  // - add all the users in the usersOrg array
  recreateDeviceUsers(device: { claimedByUid: string; usersOrg: Array<{ user: string; org: string }> }): string[] {
    if (!device || !device.claimedByUid || !device.usersOrg) {
      throw new Error('Invalid device object provided');
    }

    // Start with the owner (claimedByUid) - using a set to avoid duplicates
    const usersSet = new Set<string>();
    usersSet.add(device.claimedByUid);

    // Add all users from the usersOrg array
    device.usersOrg.forEach((entry) => {
      if (entry.user) {
        usersSet.add(entry.user);
      }
    });

    // Convert the Set back to an array (to remove duplicates automatically)
    return Array.from(usersSet);
  }


  // WARNING: this will modify the device variable, removing the organization
  public async unshareDeviceFromGroup(device: Device, groupId: string) {

    console.log(`Unsharing device ${device.id} from group ${groupId}`);

    const spinner = await this.loadingCtrl.create({});
    spinner.present();

    const batch = this.firestore.firestore.batch(); // Start a Firestore batch

    try {
      // Step 1: Find the group document by its 'id'
      const groupSnapshot = await this.firestore.firestore.collection(Globals.DB.GROUPS)
        .where('id', '==', groupId)
        .limit(2) // We expect only one result
        .get();

      if (groupSnapshot.empty) {
        throw new Error(`group with id ${groupId} not found.`);
      }

      if (groupSnapshot.size > 1) {
        throw new Error(`Multiple groups found with id ${groupId}. Operation aborted.`);
      }

      const groupDocRef = groupSnapshot.docs[0].ref;

      // Step 2: Remove the device from the organization's devices array
      batch.update(groupDocRef, {
        devices: firebase.firestore.FieldValue.arrayRemove(device.id),
      });

      // Step 3: Retrieve the device document reference
      const deviceSnapshot = await this.firestore.firestore.collection(Globals.DB.FLEET)
        .where('id', '==', device.id)
        .limit(2) // We expect only one result
        .get();

      if (deviceSnapshot.empty) {
        throw new Error(`Device with id ${device.id} not found.`);
      }

      if (deviceSnapshot.size > 1) {
        throw new Error(`Multiple devices found with id ${device.id}. Operation aborted.`);
      }

      const deviceDocRef = deviceSnapshot.docs[0].ref;
      const deviceData = deviceSnapshot.docs[0].data() as DeviceDB;
      const deviceUsersOrg: Array<{ user: string; org: string }> = deviceData['usersOrg'] || [];

      // Step 4: remove the organization users from the device->usersOrg array
      const updatedUsersOrg = deviceUsersOrg.filter((entry) => entry.org !== groupId);

      // Step 5: recreate the users array for the device
      const updatedUsers = this.recreateDeviceUsers({ claimedByUid: device.claimedByUid, usersOrg: updatedUsersOrg });

      // Step 6: Add the updates to the batch for the device document
      batch.update(deviceDocRef, {
        users: updatedUsers,
        usersOrg: updatedUsersOrg
      });

      // Step 7: Commit the batch to apply the changes
      await batch.commit();

      // // Step 8: remove the organization from the device - this is in memory, not in Firestore
      // device._groups = device._groups.filter(
      //   (org) => org.id !== groupId
      // );

      console.log(`Device ${device.id} successfully unshared from group ${groupId}`);
      this.helper.toast(`Device ${device.name} successfully unshared from group.`);
      return true;


    } catch (error) {
      console.error(`Error unsharing device from group ${groupId}:`, error);
      this.helper.toast(`Failed to unshare the device.`);
      throw new Error(`Failed to unshare device: ${error}`);
    } finally {
      spinner.dismiss();
    }
  }


  // the user parameter is the owner of the group
  public async addGroup(name: string, user: CompactUser): Promise<boolean> {

    if (name === "") {
      this.helper.toast(`Please provide a name`);
      throw new Error("group name cannot be empty");
    }

    if (user.uid === "") {
      this.helper.toast(`Please provide a user`);
      throw new Error("User uid cannot be empty");
    }

    this.loadingSubject.next(true);

    // extract the fields we need
    const emptyOrg: Group = _.cloneDeep(EMPTY_GROUP);
    let org: GroupDB = toGroupDB(emptyOrg);
    org.name = name;
    org.id = uuidv4();
    org.createdByUid = user.uid;
    org.createdAt = firebase.firestore.FieldValue.serverTimestamp();

    // add the user to the organization as an admin
    org.users = [user.uid];

    // Convert the Map to a plain object
    const usersInfoMap = new Map<string, UserNameRole>();

    // add the current user to the organization as an admin since they created it
    usersInfoMap.set(user.uid, { email: user.email, name: user.name, role: RoleType.admin, status: STATUS_ACTIVE });
    org.usersInfo = this.mapToObject(usersInfoMap);
    console.log("org", org);

    try {
      await this.firestore.firestore.collection(Globals.DB.GROUPS).add(org);
      this.helper.toast(`${org.name} has been added.`);
      return true;
    } catch (error) {
      console.log("error saving org", error);
      this.helper.toast(`We couldn't add ${org.name}. Please try again or contact support if the issue persists.`);
      return false;
    }
    finally {
      this.loadingSubject.next(false);
    }

  }


  // convert a map to a simple javascript object (when writing to Firestore)
  mapToObject(map: Map<string, UserNameRole>): any {
    const object: { [key: string]: UserNameRole } = {};
    map.forEach((value, key) => {
      object[key] = value;
    });
    return object;
  }

  // convert a simple javascript object to a map (when read from Firestore)
  objectToMap(object: any): Map<string, UserNameRole> {
    const map = new Map<string, UserNameRole>();
    Object.keys(object).forEach(key => {
      map.set(key, object[key]);
    });
    return map;
  }



  // save a device in firestore
  public async saveDevice(device: Device) {

    if (device.name === "") {
      this.helper.toast(`Please provide a name`);
      throw new Error("name cannot be empty");
    }

    if (device.id === "") {
      this.helper.toast(`Please provide an id`);
      throw new Error("id cannot be empty");
    }

    console.log("saving device", device);

    this.loadingSubject.next(true);
    const batch = this.firestore.firestore.batch(); // Start a Firestore batch

    try {
      // Step 1: Query Firestore to find the user document by the 'uid' field
      const deviceSnapshot = await this.firestore.firestore.collection(Globals.DB.FLEET)
        .where('id', '==', device.id)
        .limit(2) // We expect only one result
        .get();

      if (deviceSnapshot.size > 1) {
        throw new Error(`Multiple documents found with id ${device.id}. Operation aborted.`);
      }

      if (deviceSnapshot.empty) {
        throw new Error(`id ${device.id} not found`);
      }

      const deviceDocRef = deviceSnapshot.docs[0].ref;

      // Step 2: Update the document with the new data
      if (environment.prj === 'tidal') {
        batch.update(deviceDocRef, {
          name: device.name,
          installHeight: device.installHeight,
          timeZone: device.timeZone,
          calibrationEnabled: device.calibrationEnabled,
          rawLow: device.rawLow,
          rawHigh: device.rawHigh,
          referenceLow: device.referenceLow,
          referenceHigh: device.referenceHigh,
          windOffset: device.windOffset,
        });
      } else if (environment.prj === 'ati') {
        batch.update(deviceDocRef, {
          name: device.name,
        });
      } else {
        batch.update(deviceDocRef, {
          name: device.name,
        });
      }

      // Step 3: Commit the batch
      await batch.commit();

      this.helper.toastChangesSaved();

    } catch (error) {
      this.helper.toastErrorSaving();
      console.error(`Error saving:`, error);
      throw new Error(`Failed to save changes: ${error}`);
    } finally {
      this.loadingSubject.next(false);
    }
  }



  public async replaceDevice(device: Device, newSerialNumber: string): Promise<boolean> {

    if (!device?.id) {
      this.helper.toast("Invalid device.");
      console.error("Attempted to replace a device with an empty or undefined ID.");
      return false;
    }

    this.loadingSubject.next(true);

    try {
      console.log(`Replacing device: ${device.id} with new serial: ${newSerialNumber}`);

      await this.replaceDeviceCloudFx(device.id, newSerialNumber).toPromise();

      console.log("Device replaced successfully.");
      await this.helper.toast("Device replaced successfully.");
      return true;
    } catch (error) {
      console.error("Error replacing device:", error);
      await this.helper.toast("Error replacing device.");
      return false;
    } finally {
      this.loadingSubject.next(false);
    }
  }



  // save an org in firestore
  public async saveOrganization(organization: Group) {
    if (organization.name === "") {
      this.helper.toast(`Please provide a name`);
      throw new Error("name cannot be empty");
    }

    if (organization.id === "") {
      this.helper.toast(`Please provide an id`);
      throw new Error("id cannot be empty");
    }

    this.loadingSubject.next(true);
    const batch = this.firestore.firestore.batch(); // Start a Firestore batch

    try {
      // Step 1: Query Firestore to find the user document by the 'uid' field
      const orgSnapshot = await this.firestore.firestore.collection(Globals.DB.GROUPS)
        .where('id', '==', organization.id)
        .limit(2) // We expect only one result
        .get();

      if (orgSnapshot.size > 1) {
        throw new Error(`Multiple documents found with id ${organization.id}. Operation aborted.`);
      }

      if (orgSnapshot.empty) {
        throw new Error(`id ${organization.id} not found`);
      }

      const orgDocRef = orgSnapshot.docs[0].ref;

      // Step 2: Update the document with the new name
      batch.update(orgDocRef, {
        name: organization.name
      });

      // Step 3: Commit the batch
      await batch.commit();

      console.log(`successfully saved`);
      this.helper.toastChangesSaved();

    } catch (error) {
      this.helper.toastErrorSaving();
      console.error(`Error saving:`, error);
      throw new Error(`Failed to save changes: ${error}`);
    } finally {
      this.loadingSubject.next(false);
    }
  }


  public async saveUser(user: User) {
    if (user.name === "") {
      this.helper.toast(`Please provide a name`);
      throw new Error("User name cannot be empty");
    }

    if (user.uid === "") {
      this.helper.toast(`Please provide a user id`);
      throw new Error("User uid cannot be empty");
    }

    this.loadingSubject.next(true);
    const batch = this.firestore.firestore.batch(); // Start a Firestore batch

    try {
      // Step 1: Query Firestore to find the user document by the 'uid' field
      const userSnapshot = await this.firestore.firestore.collection(Globals.DB.USERS)
        .where('uid', '==', user.uid)
        .limit(2) // We expect only one result
        .get();

      if (userSnapshot.size > 1) {
        throw new Error(`Multiple documents found with uid ${user.uid}. Operation aborted.`);
      }

      if (userSnapshot.empty) {
        throw new Error(`User with uid ${user.uid} not found`);
      }

      const userDocRef = userSnapshot.docs[0].ref;

      // Step 2: Update the user document with the new name
      batch.update(userDocRef, {
        name: user.name
      });

      // Step 3: Query all organizations where this user is referenced
      const orgsSnapshot = await this.firestore.firestore.collection(Globals.DB.GROUPS)
        .where(`usersInfo.${user.uid}`, '!=', null) // Find all orgs that reference this user
        .get();

      // Step 4: Update the name of the user in each organization
      orgsSnapshot.forEach((orgDoc) => {
        const orgDocRef = orgDoc.ref;
        const orgData = orgDoc.data();

        if (orgData['usersInfo'] && orgData['usersInfo'][user.uid]) {
          orgData['usersInfo'][user.uid].name = user.name; // Update the user's name in the org's usersInfo map

          // Add the update to the batch
          batch.update(orgDocRef, {
            [`usersInfo.${user.uid}.name`]: user.name
          });
        }
      });

      // Step 5: Commit the batch
      await batch.commit();

      console.log(`User successfully saved and updated in organizations`);
      this.helper.toastChangesSaved();

    } catch (error) {
      this.helper.toastErrorSaving();
      console.error(`Error saving user:`, error);
      throw new Error(`Failed to save user changes: ${error}`);
    } finally {
      this.loadingSubject.next(false);
    }
  }



  public async saveClaimsOnUser(email: string, claims: string) {
    if (email === "") {
      this.helper.toast(`Please provide an email`);
      throw new Error("User email cannot be empty");
    }

    this.loadingSubject.next(true);

    console.log(`email: ${email}, claims: ${claims}`);

    try {
      // Step 1: Query Firestore to find the user document by the email field
      const userSnapshot = await this.firestore.firestore.collection(Globals.DB.USERS)
        .where('email', '==', email)
        .limit(2) // We expect only one result
        .get();

      if (userSnapshot.size > 1) {
        throw new Error(`Multiple documents found with email ${email}. Operation aborted.`);
      }

      if (userSnapshot.empty) {
        throw new Error(`User with email ${email} not found`);
      }

      const userDocRef = userSnapshot.docs[0].ref;

      // Step 2: Update the user document with the new name
      userDocRef.update({ claims: claims });
      console.log(`User claims successfully saved with claims: ${claims}`);

    } catch (error) {
      this.helper.toastErrorSaving();
      console.error(`Error saving user claims:`, error);
      throw new Error(`Failed to save user claims: ${error}`);
    } finally {
      this.loadingSubject.next(false);
    }
  }



  /****************************************************************************************************
  
  ########  ######## ##       ######## ######## ######## 
  ##     ## ##       ##       ##          ##    ##       
  ##     ## ##       ##       ##          ##    ##       
  ##     ## ######   ##       ######      ##    ######   
  ##     ## ##       ##       ##          ##    ##       
  ##     ## ##       ##       ##          ##    ##       
  ########  ######## ######## ########    ##    ######## 
    
  ****************************************************************************************************/

  public async unclaimDevice(device: Device) {

    if (device.firestoreDocId === "") {
      this.helper.toast(`Please provide a device`);
      throw new Error("Device cannot be empty");
    }

    const spinner = await this.loadingCtrl.create({});
    spinner.present();

    try {
      const result = await this.unclaimDeviceCloudFx(device).toPromise();
      await this.helper.toast("Device unclaimed.");
      return true;
    } catch (error) {
      console.error('Error:', error);
      await this.helper.toast("Error unclaiming device.");
      return false;
    }
    finally {
      spinner.dismiss();
    }

  }

  async sendTestEmail(to: string) {
    this.loadingSubject.next(true);
    try {
      let response = await this.sendTestEmailCloudFx(to).toPromise();
      console.log("response", response);
      if (response) {
        await this.helper.toast("Email sent.");
      } else {
        await this.helper.toast("Failed to send email.");
      }
    } catch (error) {
      await this.helper.toast("Failed to send email.");
    }
    finally {
      this.loadingSubject.next(false);
    }
  }

  async sendInviteEmail(to: string) {
    this.loadingSubject.next(true);
    try {
      let response = await this.sendInviteEmailCloudFx(to).toPromise();
      console.log("response", response);
      if (response) {
        await this.helper.toast("Email sent.");
      } else {
        await this.helper.toast("Failed to send email.");
      }
    } catch (error) {
      await this.helper.toast("Failed to send email.");
    }
    finally {
      this.loadingSubject.next(false);
    }
  }

  async sendTestSms(sms: Sms) {
    await this.helper.toast("Coming soon.");
    // this.loadingSubject.next(true);
    // try {
    //   let response = await this.sendSmsCloudFx(sms).toPromise();
    //   if (response) {
    //     await this.helper.toast("SMS sent.");
    //   } else {
    //     await this.helper.toast("Failed to send SMS.");
    //   }
    // } catch (error) {
    //   await this.helper.toast("Failed to send SMS.");
    // }
    // finally {
    //   this.loadingSubject.next(false);
    // }
  }


  /****************************************************************************************************
    
       ######     ###    ##       ##          ###    ########  ##       ########       ######## ##     ## 
      ##    ##   ## ##   ##       ##         ## ##   ##     ## ##       ##             ##        ##   ##  
      ##        ##   ##  ##       ##        ##   ##  ##     ## ##       ##             ##         ## ##   
      ##       ##     ## ##       ##       ##     ## ########  ##       ######         ######      ###    
      ##       ######### ##       ##       ######### ##     ## ##       ##             ##         ## ##   
      ##    ## ##     ## ##       ##       ##     ## ##     ## ##       ##             ##        ##   ##  
       ######  ##     ## ######## ######## ##     ## ########  ######## ########       ##       ##     ## 
    
  ****************************************************************************************************/
  // call a Particle cloud function
  callParticleFunctionCloudFx(deviceId: string, functionName: string, arg: string) {
    const callable = this.angularFireFunctions.httpsCallable('callParticleFunction');
    return callable({ deviceId: deviceId, functionName: functionName, arg: arg })
  }

  setClaimsOnUserCloudFx(email: string, claim: string) {
    const callable = this.angularFireFunctions.httpsCallable('setClaimsOnUser');
    return callable({ email: email, claim: claim });
  }

  replaceDeviceCloudFx(oldDeviceId: string, newSerialNumber: string) {
    const callable = this.angularFireFunctions.httpsCallable('replaceDevice');
    return callable({ oldDeviceId: oldDeviceId, newSerialNumber: newSerialNumber });
  }

  inviteUserCloudFx(userInvited: UserInvited) {
    // this uid will be used since the user does not exist in the system yet
    const tempUid = uuidv4();
    const callable = this.angularFireFunctions.httpsCallable('inviteUser');
    return callable({
      tempUid: tempUid, email: userInvited.email.toLowerCase(), groupId: userInvited.groupId,
      role: userInvited.role, inviterName: this.userInfo.name
    });
  }

  addUserToGroupCloudFx(userInvited: UserInvited) {
    // this uid will be used if the user does not exist in the system yet
    const tempUid = uuidv4();
    const callable = this.angularFireFunctions.httpsCallable('addUserToGroup');
    return callable({
      tempUid: tempUid, email: userInvited.email.toLowerCase(), groupId: userInvited.groupId,
      role: userInvited.role, inviterName: this.userInfo.name
    });
  }

  removeUserFromGroupAndDevicesCloudFx(userUid: string, groupId: string) {
    const callable = this.angularFireFunctions.httpsCallable('removeUserFromGroupAndDevices');
    return callable({ removedUserUid: userUid, groupId: groupId });
  }

  deleteUserCloudFx(email: string) {
    const callable = this.angularFireFunctions.httpsCallable('deleteUser');
    return callable({ email: email.toLowerCase() });
  }

  deleteGroupCloudFx(groupId: string) {
    const callable = this.angularFireFunctions.httpsCallable('deleteGroup');
    return callable({ groupId: groupId });
  }

  // send email
  sendInviteEmailCloudFx(to: string) {
    const callable = this.angularFireFunctions.httpsCallable('sendInviteEmail');
    return callable({ to: to });
  }

  // send email
  sendTestEmailCloudFx(to: string) {
    const callable = this.angularFireFunctions.httpsCallable('sendTestEmail');
    return callable({ to: to });
  }

  // send SMS
  sendSmsCloudFx(sms: Sms) {
    const callable = this.angularFireFunctions.httpsCallable('sendSms');
    return callable({ sms });
  }

  unclaimDeviceCloudFx(device: Device) {
    const callable = this.angularFireFunctions.httpsCallable('unclaimDevice');
    return callable({ deviceId: device.id });
  }

}

